import { cva, type VariantProps } from "class-variance-authority";
import { ChevronRight } from "lucide-react";
import type { ComponentPropsWithRef, ReactNode } from "react";

import { cn } from "@/lib/cn";
import { FOCUS, PRESS_FLAT } from "@/lib/interaction";

const row = cva(
  // 행은 크기를 줄이지 않는다. 가로로 꽉 찬 것이 오그라들면 옆 행과 어긋나
  // 보인다. 대신 눌린 순간 배경을 한 단계 더 짙게 한다.
  `flex w-full items-center text-left disabled:pointer-events-none disabled:opacity-45 focus-visible:outline-offset-[-2px] ${PRESS_FLAT} ${FOCUS}`,
  {
    variants: {
      tone: {
        default: "text-ink hover:bg-paper/70 active:bg-chip",
        danger: "text-danger hover:bg-danger/8 active:bg-danger/14",
      },
      size: {
        /** 내 프로필 팝오버 */
        sm: "gap-2.75 px-3.5 py-2.25 text-sm",
        /** 상대 프로필 모달 */
        md: "gap-3 px-6 py-2.75 text-sm",
      },
    },
    defaultVariants: { tone: "default", size: "md" },
  },
);

type ListRowProps = VariantProps<typeof row> & {
  /** 왼쪽 아이콘 자리. 아바타든 이모지든 넣는다. */
  leading?: ReactNode;
  children: ReactNode;
  /** 이름 옆에 흐리게 붙는 수 — "디자인팀 3", "받은 파일 12" */
  count?: number | undefined;
  /** 오른쪽 › 를 보일지. 눌러서 **옆으로** 열리는 행에만 붙인다. */
  chevron?: boolean;
  /**
   * 오른쪽 끝에 직접 넣을 것. 주면 `chevron`을 대신한다.
   *
   * 화살표가 가리키는 방향과 실제로 열리는 방향이 달라지면 안 되기 때문에
   * 있다 — 같은 행이 넓은 화면에서는 옆으로, 좁은 화면에서는 아래로 열린다.
   */
  trailing?: ReactNode;
  // ref를 받는다. 이 행이 팝오버를 여는 트리거로 쓰이면 Radix가 위치를 재려고
  // 실제 DOM 노드를 필요로 한다.
} & Omit<ComponentPropsWithRef<"button">, "children">;

export function ListRow({
  leading,
  children,
  count,
  chevron = false,
  trailing,
  tone,
  size,
  className,
  type = "button",
  ...rest
}: ListRowProps) {
  return (
    <button type={type} className={cn(row({ tone, size }), className)} {...rest}>
      {leading ? (
        <span className="flex w-4.5 shrink-0 items-center justify-center [&_svg]:size-4">
          {leading}
        </span>
      ) : null}

      {/* min-w-0가 없으면 긴 이름이 줄을 늘려 창 전체를 밀어낸다 */}
      <span className="line-clamp-1-ko min-w-0 flex-1">
        {children}
        {count === undefined ? null : (
          <span className="text-muted-2 ml-1.5 font-normal">{count}</span>
        )}
      </span>

      {trailing ?? (chevron ? <ChevronRight className="text-faint size-4 shrink-0" /> : null)}
    </button>
  );
}
